import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "@phosphor-icons/react";

const SignatureFlyerPage = () => {
  useEffect(() => {
    // Set page title for printing
    document.title = "Signature Freehand Blonde Placement - Flyer";
  }, []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link 
            to="/education" 
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Zurück zu Events</span>
          </Link>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-[#9A8C73] text-white px-4 py-2 rounded-lg hover:bg-[#7A6C53] transition-colors"
          >
            <Printer size={20} />
            <span>Drucken / PDF speichern</span>
          </button>
        </div>
      </div>

      {/* Flyer Content */}
      <div className="max-w-[210mm] mx-auto bg-white shadow-xl print:shadow-none print:max-w-none">
        <div className="p-8 print:p-6" style={{ minHeight: "297mm" }}>
          
          {/* Header */}
          <div className="bg-[#E07B4C] text-white py-3 px-6 text-center mb-6 -mx-8 -mt-8 print:-mx-6 print:-mt-6">
            <p className="text-sm tracking-[0.3em] uppercase font-medium">
              EDUCATOR CLASSES | JENNIFER SAILER
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:grid-cols-2">
            
            {/* Left Column - Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="rounded-lg overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_b7becaad-2199-4d6f-9a45-a3a49b173ffd/artifacts/xbvvq84c_IMG_1230.jpeg"
                  alt="Freehand Balayage Technik"
                  className="w-full h-auto object-cover"
                />
              </div>
              
              {/* Second Image - Result */}
              <div className="rounded-lg overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=300&fit=crop"
                  alt="Blonde Ergebnis"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>

            {/* Right Column - Content */}
            <div className="space-y-4">
              {/* Title */}
              <div>
                <h1 className="text-[#E07B4C] text-xl font-bold uppercase tracking-wide">
                  SIGNATURE FREEHAND BLONDE PLACEMENT
                </h1>
                <p className="text-gray-800 font-semibold mt-1">
                  HANDS-ON MASTERCLASS
                </p>
              </div>

              {/* Main Description */}
              <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                <p>
                  <strong>Das moderne Painting-System für weiche, dimensionale & luxuriöse Übergänge.</strong> Ohne Toupage. Ohne künstliche Root-Shadows. Ohne Assistenz.
                </p>
                <p>
                  Dein persönliches Freehand Balayage System für den realen Salonalltag. Es verbindet individuelles Sectioning, präzises Placement und effiziente Workflows zu einem klaren, reproduzierbaren Ablauf – für unterschiedliche Blond-Charaktere aus einer Technik.
                </p>
              </div>

              {/* Das Ergebnis */}
              <div className="text-sm text-gray-700">
                <p className="font-bold text-gray-800">Das Ergebnis:</p>
                <p>
                  Natürlich wirkende Blond-Ergebnisse, gesündere Längen, weniger Zeitaufwand und maximale Kontrolle – auch beim Arbeiten alleine.
                </p>
              </div>

              {/* Target Audience */}
              <div className="text-sm text-gray-700">
                <p className="font-bold text-gray-800">Diese Masterclass richtet sich an Stylist:innen, die:</p>
                <ul className="mt-1 space-y-0.5">
                  <li>• bereits Blond-/Painting-Grundkenntnisse haben</li>
                  <li>• schneller und strukturierter arbeiten möchten</li>
                  <li>• auf Toupage & Root-Shadow-Abhängigkeit verzichten wollen</li>
                  <li>• ihre Technik professionalisieren und individualisieren wollen</li>
                  <li>• Ergebnisse möchten, die im Alltag reproduzierbar sind</li>
                </ul>
              </div>

              {/* Praxis Info */}
              <p className="text-sm text-gray-700">
                Du arbeitest praxisnah am eigenen Modell, bekommst persönliches Feedback und entwickelst ein Konzept, das im Salon überzeugt – und online sichtbar wirkt.
              </p>

              {/* Details Grid */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div className="flex">
                    <span className="text-gray-500 w-16">Artist:</span>
                    <span className="text-gray-800">@jennifervallentin</span>
                  </div>
                  <div></div>
                  <div className="flex">
                    <span className="text-gray-500 w-16">Wann:</span>
                    <span className="text-gray-800">22.06 | 14.09</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-16">Wo:</span>
                    <span className="text-gray-800">74211 Leingarten</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-16">TN:</span>
                    <span className="text-gray-800">max. 10 Teilnehmer</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-16">Preis:</span>
                    <span className="text-gray-800">590 € inkl. MwSt.</span>
                  </div>
                </div>
              </div>

              {/* QR Code Section */}
              <div className="flex items-end justify-end mt-4">
                <div className="text-right">
                  <div className="bg-gray-100 p-3 rounded-lg inline-block">
                    {/* QR Code Placeholder - in real use, generate actual QR */}
                    <div className="w-20 h-20 bg-white border-2 border-gray-300 flex items-center justify-center">
                      <svg viewBox="0 0 100 100" className="w-16 h-16">
                        <rect x="10" y="10" width="20" height="20" fill="#333"/>
                        <rect x="70" y="10" width="20" height="20" fill="#333"/>
                        <rect x="10" y="70" width="20" height="20" fill="#333"/>
                        <rect x="40" y="10" width="10" height="10" fill="#333"/>
                        <rect x="10" y="40" width="10" height="10" fill="#333"/>
                        <rect x="50" y="40" width="10" height="10" fill="#333"/>
                        <rect x="70" y="40" width="10" height="10" fill="#333"/>
                        <rect x="40" y="50" width="10" height="10" fill="#333"/>
                        <rect x="60" y="60" width="10" height="10" fill="#333"/>
                        <rect x="80" y="60" width="10" height="10" fill="#333"/>
                        <rect x="40" y="70" width="10" height="10" fill="#333"/>
                        <rect x="60" y="80" width="10" height="10" fill="#333"/>
                        <rect x="15" y="15" width="10" height="10" fill="white"/>
                        <rect x="75" y="15" width="10" height="10" fill="white"/>
                        <rect x="15" y="75" width="10" height="10" fill="white"/>
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-[#E07B4C] font-bold mt-2 uppercase tracking-wide">
                    Jetzt informieren<br />& buchen
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-[10px] text-gray-400 text-center">
              Dieses Seminar ist ein eigenständiges Angebot unserer Trainer:innen, die Inhalt, Durchführung und Rechnungsstellung verantworten.
            </p>
          </div>

        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SignatureFlyerPage;
